import { Close, Search } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import branch from "../../services/branch";
import sales from "../../services/sales";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

let thisYear = new Date().getFullYear();
let allYears: number[] = [];
for (let x = 0; x <= 20; x++) {
  allYears.push(thisYear - x);
}

export default function MonthlySales() {
  const [pageSize, setPageSize] = useState<number>(30);
  const [options, setOptions] = useState<any>({
    keyword: "",
  });
  const listBranch = useQuery(
    ["branch", options],
    ({ queryKey }) => {
      return branch.getAllBranch({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  const onHandleSearch = useCallback((values: { keyword: any }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);

  const form = useForm({
    defaultValues: {
      keyword: "",
    },
  });

  const [options2, setOptions2] = useState<any>({
    branch_ids: [],
    year: new Date().getFullYear(),
  });

  const form2 = useForm({
    defaultValues: options2,
  });

  const downloadMonthlySalesYearly = useMutation(
    "download-monthly-sales-yearly",
    sales.downloadMonthlySalesYearly
  );
  const downloadGroupMonthlySalesYearly = useMutation(
    "download-group-monthly-sales-yearly",
    sales.downloadGroupMonthlySalesYearly
  );
  const onHandleDownloadMonthlySalesYearly = (params: any) => downloadMonthlySalesYearly.mutate(params);
  const onHandleGroupDownloadMonthlySalesYearly = (params: any) => downloadGroupMonthlySalesYearly.mutate(params);

  return (
    <MainLayout>
      <Helmet>
        <title>Monthly Sales</title>
      </Helmet>

      <Breadcrumb
        label="Monthly Sales"
        breadcrumbs={[{ label: "Report", href: "/app/report" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2, 10]}>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <Controller
              name="keyword"
              control={form.control}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Input Keyword here"
                  {...field}
                  {...config.onEnter(() => {
                    form.handleSubmit(onHandleSearch)();
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={() => {
                          if (field.value) {
                            field.onChange("");
                            setOptions((prev: any) => ({
                              ...prev,
                              keyword: "",
                            }));
                          }
                        }}
                      >
                        {field.value ? (
                          <Close color="error" sx={{ cursor: "pointer" }} />
                        ) : (
                          <Search />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              variant="outlined"
              onClick={form.handleSubmit(onHandleSearch)}
            >
              SEARCH
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                const allBranchIds = get(listBranch, "data.data", []).map((branch: any) => branch.id);
                form2.setValue("branch_ids", allBranchIds);
                setOptions2({
                  ...options2,
                  branch_ids: allBranchIds,
                });
              }}>
              SELECT ALL
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                form2.setValue("branch_ids", []);
                setOptions2({
                  ...options2,
                  branch_ids: [],
                });
              }}>
              UNSELECT ALL
            </Button>
          </Stack>
        </Box>
        <Box sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1}>
            <FormControl margin="normal">
              <FormLabel>Year</FormLabel>
              <Select
                id="year"
                value={form2.watch("year")}
                onChange={(event) => {
                  form2.setValue("year", Number(event.target.value));
                }}
                defaultValue={form2.watch("year")}
              >
                <MenuItem key={0} value={0}>
                  Select Year
                </MenuItem>
                {allYears.map((x) => (
                  <MenuItem key={x} value={x}>
                    {x}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LoadingButton
                style={{ marginTop: "20px" }}
                variant="contained"
                onClick={form2.handleSubmit(onHandleDownloadMonthlySalesYearly)}
                loading={downloadMonthlySalesYearly.isLoading}
              >
                Download Monthly Sales Yearly
              </LoadingButton>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LoadingButton
                style={{ marginTop: "20px" }}
                variant="contained"
                onClick={form2.handleSubmit(onHandleGroupDownloadMonthlySalesYearly)}
                loading={downloadGroupMonthlySalesYearly.isLoading}
              >
                Download Group Monthly Sales Yearly
              </LoadingButton>
            </Box>
          </Stack>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6">
            Selected Branches: {form2.getValues("branch_ids")?.map((id: number) => get(listBranch, "data.data", []).find((branch: any) => branch.id === id)?.name).join(", ") || "None (will select all)"}
          </Typography>
        </Box>
        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listBranch.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={get(listBranch, "data.data", [])}
            rowCount={get(listBranch, "data.data", []).length}
            page={options.page_number}
            pageSize={pageSize}
            rowsPerPageOptions={[30, 50, 100]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              {
                field: "group_name",
                headerName: "Group",
                flex: 1,
                filterable: false,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "name",
                headerName: "Name",
                flex: 1,
                filterable: false,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography>{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "place",
                headerName: "Place",
                flex: 1,
                filterable: false,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack>
                      <Typography >{params.value}</Typography>
                    </Stack>
                  );
                },
              },
              {
                field: "actions",
                headerName: "Action",
                sortable: false,
                filterable: false,
                width: 200,
                renderCell: (params) => {
                  return (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          var currentBranches = form2.getValues("branch_ids") || [];
                          if (!currentBranches?.includes(get(params, "row.id"))) {
                            currentBranches.push(get(params, "row.id"));
                          } else {
                            currentBranches.splice(currentBranches.indexOf(get(params, "row.id")), 1);
                          }
                          form2.setValue("branch_ids", currentBranches);
                          setOptions2({
                            ...options2,
                            branch_ids: currentBranches,
                          });
                        }}
                      >
                        {form2.getValues("branch_ids")?.includes(get(params, "row.id")) ? "Unselect" : "Select"}
                      </Button>
                    </Stack>
                  );
                },
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
